/**
 * AbilityCreator — the first Vibe write UI (Sprint U2.4).
 *
 * Owner-only on the server. From the deployed guest bundle a submit returns
 * 403 until the cross-origin owner session is wired (see U2.4 handoff); the
 * form renders + validates regardless, and works end-to-end once an owner
 * session cookie is present.
 */

import { useState, type FormEvent } from "react";

import {
  ApiError,
  createAbility,
  type AbilityCreateInput,
  type AbilityDTO,
} from "../api.js";

// Mirrors ODYSSEY_ABILITY_SYSTEM_TYPES in @vi/db (frontend can't import server pkgs).
const SYSTEM_TYPES = ["innate", "cultivated", "tech_assisted", "cosmic"] as const;
type SystemType = (typeof SYSTEM_TYPES)[number];

type SubmitState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error"; message: string };

/** Split a textarea into a trimmed, non-empty string list (one entry per line). */
function linesToList(value: string): string[] {
  return value
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function AbilityCreator({
  onCreated,
  onCancel,
}: {
  onCreated: (ability: AbilityDTO) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [systemType, setSystemType] = useState<SystemType>("innate");
  const [description, setDescription] = useState("");
  const [restrictions, setRestrictions] = useState("");
  const [weaknesses, setWeaknesses] = useState("");
  const [prerequisites, setPrerequisites] = useState("");
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  const canSave = name.trim().length > 0 && submit.kind !== "saving";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    setSubmit({ kind: "saving" });

    const desc = description.trim();
    const input: AbilityCreateInput = {
      name: name.trim(),
      systemType,
      description: desc ? [{ tier: "tier_1", text: desc }] : undefined,
      restrictions: linesToList(restrictions),
      weaknesses: linesToList(weaknesses).map((text) => ({ tier: "tier_1" as const, text })),
      prerequisites: linesToList(prerequisites),
    };

    try {
      const ability = await createAbility(input);
      onCreated(ability);
    } catch (err) {
      let message: string;
      if (err instanceof ApiError) {
        message =
          err.status === 403
            ? "Owner sign-in required to write canon. The cross-origin owner session isn't wired yet (U2.4) — the route works with an owner session cookie."
            : err.message;
      } else {
        message = err instanceof Error ? err.message : "Unknown error";
      }
      setSubmit({ kind: "error", message });
    }
  }

  return (
    <form className="creator-form" onSubmit={handleSubmit}>
      <h2>New ability</h2>

      <label>
        <span>Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Wordcraft"
          autoFocus
        />
      </label>

      <label>
        <span>System type</span>
        <select value={systemType} onChange={(e) => setSystemType(e.target.value as SystemType)}>
          {SYSTEM_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label>
        <span>Description</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="What the ability is and how it works…"
        />
      </label>

      <label>
        <span>
          Restrictions <em>(one per line)</em>
        </span>
        <textarea value={restrictions} onChange={(e) => setRestrictions(e.target.value)} rows={2} />
      </label>

      <label>
        <span>
          Weaknesses <em>(one per line)</em>
        </span>
        <textarea value={weaknesses} onChange={(e) => setWeaknesses(e.target.value)} rows={2} />
      </label>

      <label>
        <span>
          Prerequisites <em>(one per line)</em>
        </span>
        <textarea
          value={prerequisites}
          onChange={(e) => setPrerequisites(e.target.value)}
          rows={2}
        />
      </label>

      {submit.kind === "error" && <p className="form-error">{submit.message}</p>}

      <div className="form-actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={!canSave}>
          {submit.kind === "saving" ? "Saving…" : "Create ability"}
        </button>
      </div>
    </form>
  );
}
